import { useToastProvider } from "@deere/fuel-react";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import FileDownloadedNotification from "./FileDownloadedNotification";
import promotionsApi from "../../../../../../../../api/promotions/promotionsApi";
import usePromotionsApi from "../../../../../../../../api/promotions/usePromotionsApi";
import { ConfigContext } from "../../../../../../../../context/ConfigContext";
import { Notification } from "../../hooks/useNotifications/useNotifications";

interface PromotionsNotificationProps {
  notification: Notification;
  expireNotification: (notificationId: number) => void;
  downloadFile: (url: string, fileName: string) => void;
}

const generateS3AttachmentUrl = (
  promotionServiceUrl: any,
  authToken: string | undefined,
  jobId: string,
  fileDownloadNotAvailableToast: () => void,
  fileDownloadFailureToast: () => void,
  isRBACAuthEnabled: boolean,
  promotionsApiHook: ReturnType<typeof usePromotionsApi>
): Promise<string | void> =>
  isRBACAuthEnabled
    ? promotionsApiHook
        .generateS3AttachmentUrl(jobId)
        .then((response) => response ?? "")
        .catch((error) => {
          if (error.response?.data.status === 404) {
            fileDownloadNotAvailableToast();
          } else {
            fileDownloadFailureToast();
          }
        })
    : promotionsApi
        .generateS3AttachmentUrl(promotionServiceUrl, authToken ?? "", jobId)
        .then((response) => response ?? "")
        .catch((error) => {
          if (error.response?.data.status === 404) {
            fileDownloadNotAvailableToast();
          } else {
            fileDownloadFailureToast();
          }
        });

const PromotionsNotification = ({
  notification,
  expireNotification,
  downloadFile,
}: PromotionsNotificationProps) => {
  const { t } = useTranslation("common");
  const {
    PROMOTION_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_PROMOTIONS,
  } = useContext(ConfigContext);
  const { enqueueToast } = useToastProvider();
  const isRBACAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_PROMOTIONS;
  const promotionsApiHook = usePromotionsApi();

  const { filename, id } = notification.payload;
  const successMessage = t(
    "notifications.promotionFileDownloadSuccessNotificationMessage",
    { filename }
  );
  const failureMessage = t(
    "notifications.promotionFileDownloadFailureNotificationMessage",
    { filename }
  );

  const fileDownloadNotAvailableToast = () => {
    enqueueToast({
      variant: "error",
      message: t("notifications.download.promotionFile.not.available") ?? "",
      isCloseable: true,
    });
  };

  const fileDownloadFailureToast = () => {
    enqueueToast({
      variant: "error",
      message: t("notifications.download.promotionFile.error.toast") ?? "",
      isCloseable: true,
    });
  };

  const generateS3AttachmentLink = (accessToken: string) =>
    generateS3AttachmentUrl(
      PROMOTION_SERVICE_URL,
      accessToken,
      id,
      fileDownloadNotAvailableToast,
      fileDownloadFailureToast,
      isRBACAuthEnabled,
      promotionsApiHook
    ).then((attachmentUrl) => {
      if (attachmentUrl) {
        downloadFile(attachmentUrl, filename);
      }
    });

  return (
    <FileDownloadedNotification
      notification={notification}
      expireNotification={() =>
        expireNotification(new Date(notification.createdAt).getTime())
      }
      successMessage={successMessage}
      failureMessage={failureMessage}
      downloadFunction={generateS3AttachmentLink}
    />
  );
};

export default PromotionsNotification;
